import { useEffect, useMemo, useRef, useState } from "react";
import "./SearchableSelect.css";

// Drop-in replacement for a plain <select> that lets the user type to
// filter the option list instead of scrolling a long native dropdown —
// options: [{ value, label }], value/onChange behave like a controlled
// <select>.
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  className = "",
  emptyMessage = "No matches found",
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)),
    [options, value]
  );

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Flip the options panel above the trigger when there isn't enough room
  // below (e.g. the field sits near the bottom of the viewport after
  // scrolling) — otherwise it opens off-screen / gets clipped.
  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const PANEL_HEIGHT = 280;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, query]);

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  return (
    <div className={`searchableSelect ${className}`.trim()} ref={wrapperRef}>
      <button
        type="button"
        className={`searchableSelect__trigger ${disabled ? "searchableSelect__trigger--disabled" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        <span className={selected ? "searchableSelect__value" : "searchableSelect__placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="searchableSelect__chevron">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={`searchableSelect__panel ${openUp ? "searchableSelect__panel--up" : ""}`}>
          <input
            ref={inputRef}
            type="text"
            className="searchableSelect__search"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="searchableSelect__list">
            {filtered.length === 0 ? (
              <div className="searchableSelect__empty">{emptyMessage}</div>
            ) : (
              filtered.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  className={`searchableSelect__item ${String(opt.value) === String(value) ? "searchableSelect__item--active" : ""}`}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
